import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getIntegrations, upsertIntegration } from '@/services/api';
import type { IntegrationSetting } from '@/types';

const TYPE_LABELS: Record<number, string> = { 1: 'Email Ingest', 2: 'Email Send' };

export default function IntegrationSettings() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<IntegrationSetting | null>(null);

  const { data: settings, isLoading } = useQuery({ queryKey: ['integrations'], queryFn: getIntegrations });

  const save = useMutation({
    mutationFn: (s: IntegrationSetting) =>
      upsertIntegration(s.key, {
        integrationType: s.integrationType,
        key: s.key,
        valueRef: s.valueRef,
        isEnabled: s.isEnabled,
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['integrations'] }); setEditing(null); },
  });

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">Integration Settings</h1>
      {isLoading && <p className="text-brand-text-secondary">Loading…</p>}

      <div className="grid gap-4">
        {settings?.map(s => (
          <div key={s.integrationSettingId} className="bg-white rounded shadow p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-semibold text-sm">{s.key}</p>
                <p className="text-xs text-brand-text-secondary">{TYPE_LABELS[s.integrationType]} — {s.isEnabled ? 'Enabled' : 'Disabled'}</p>
              </div>
              <button onClick={() => setEditing(s)} className="text-brand-text-primary text-sm hover:underline">Edit</button>
            </div>

            {editing?.integrationSettingId === s.integrationSettingId ? (
              <div className="mt-3 space-y-2">
                <label className="block text-sm">
                  Secrets Manager ARN
                  <input
                    value={editing.valueRef}
                    onChange={e => setEditing(ex => ex ? { ...ex, valueRef: e.target.value } : ex)}
                    placeholder="arn:aws:secretsmanager:…"
                    className="mt-1 w-full border rounded px-3 py-1.5 text-sm font-mono"
                  />
                </label>
                <div className="flex items-center gap-3">
                  <label className="text-sm flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={editing.isEnabled}
                      onChange={e => setEditing(ex => ex ? { ...ex, isEnabled: e.target.checked } : ex)}
                    /> Enabled
                  </label>
                  <button
                    onClick={() => editing && save.mutate(editing)}
                    disabled={save.isPending}
                    className="px-3 py-1 bg-brand-accent text-brand-text-inverse rounded text-sm disabled:opacity-40"
                  >Save</button>
                  <button onClick={() => setEditing(null)} className="px-3 py-1 border rounded text-sm">Cancel</button>
                </div>
              </div>
            ) : (
              <p className="mt-2 text-xs font-mono text-brand-text-secondary break-all">{s.valueRef || '—'}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
